/**
 * One decoded line of an agent stream: an event name plus its payload. `D` is the union
 * of `data` shapes that particular endpoint emits — each event reads only its own fields.
 */
export interface StreamEvent<D> {
  event?: string;
  data?: D;
}

/** Parse a stream line, returning null on malformed JSON rather than throwing. */
function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Decode `body` line by line, yielding every line that parses into an event. */
async function* readEvents<D>(body: ReadableStream<Uint8Array>): AsyncGenerator<StreamEvent<D>> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  /** Null for a blank or malformed line — the newline boundary guards completeness. */
  const toEvent = (line: string): StreamEvent<D> | null => {
    if (!line) return null;
    // Tolerate an optional SSE-style `data: ` prefix.
    const payload = line.startsWith("data:") ? line.slice(5).trim() : line;
    return safeJsonParse<StreamEvent<D>>(payload);
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete lines; keep the trailing partial line in the buffer.
    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      const event = toEvent(buffer.slice(0, newlineIndex).trim());
      buffer = buffer.slice(newlineIndex + 1);
      if (event) yield event;
    }
  }

  // A final line the server didn't terminate with a newline.
  const trailing = toEvent(buffer.trim());
  if (trailing) yield trailing;
}

/**
 * The agent's line-delimited JSON event stream, as an async iterable of decoded events.
 * Shared by every endpoint that streams (see `transport.ts`, `capabilities.ts`) so the
 * wire format — chunk buffering, the optional `data:` prefix, malformed-line tolerance —
 * has exactly one implementation.
 *
 * `label` names the caller in the empty-body error (e.g. "Agent request"). The body check
 * runs eagerly, so that failure surfaces at the call rather than at first iteration.
 */
export function parseEventStream<D>(
  response: Response,
  label: string,
): AsyncGenerator<StreamEvent<D>> {
  const body = response.body;
  if (!body) {
    throw new Error(`${label} failed: empty response body`);
  }
  return readEvents<D>(body);
}
